import { compact } from 'lodash';
import React, { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { UserContext } from 'venn-components';
import type { TableRowMenuOption } from 'venn-ui-kit';
import { TableRowOptionsMenu, Tooltip } from 'venn-ui-kit';
import { MANAGE_DATA_SECTION, type ObjectId, navigateToManageDataPage, useHasFF, useModal } from 'venn-utils';
import ConfigureBenchmarksModal from './ConfigureBenchmarksModal';
import PrivateDeleteConfirmationModal from './PrivateDeleteConfirmationModal';
import type { PrivatesTableRow, PrivatesUpdateData } from './privatesColumns';

interface PrivatesActionMenuProps {
  item: PrivatesTableRow | undefined;
  clearSelectedItems: () => void;
  updateData: PrivatesUpdateData;
}

export const PrivatesActionMenu = ({ item, updateData, clearSelectedItems }: PrivatesActionMenuProps) => {
  const history = useHistory();
  const hasIndividualBenchmarksVisibilityFF = useHasFF('individual_benchmarks_visibility_ff');
  const { hasPermission, hasPermissionForResource } = useContext(UserContext);
  const [privateDeleteConfirmationModalOpen, openPrivateDeleteConfirmationModal, closePrivateDeleteConfirmationModal] =
    useModal();
  const [showConfigureBenchmarksModal, openConfigureBenchmarkModal, closeConfigureBenchmarkModal] = useModal();
  const onDeleteComplete = (deletedItems?: PrivatesTableRow[]) => {
    clearSelectedItems();
    updateData(deletedItems);
    closePrivateDeleteConfirmationModal();
  };

  const isDeleteDisabled =
    item &&
    (!item.userUploaded ||
      (item.ownerId ? item.ownerId < 0 : false) ||
      !hasPermissionForResource('EDIT_PORTFOLIO', item));

  const privateObject: ObjectId =
    item?.itemType === 'INVESTMENT' ? { privateFundId: item?.id } : { privatePortfolioId: item?.id };

  const options: TableRowMenuOption[] = item
    ? compact([
        {
          label: 'Manage Data',
          onClick: () => navigateToManageDataPage(history, privateObject, 'Library', false),
        },
        hasPermission('CONFIGURE_BENCHMARKS') &&
          !hasIndividualBenchmarksVisibilityFF && {
            label: 'Configure Benchmarks',
            onClick: openConfigureBenchmarkModal,
          },
        {
          label: 'CF Pacing Model Parameters',
          onClick: () =>
            navigateToManageDataPage(
              history,
              privateObject,
              'Library',
              false,
              MANAGE_DATA_SECTION.CASH_FLOW_PACING_SETTINGS,
            ),
        },
        {
          label: 'Delete',
          disabled: isDeleteDisabled,
          onClick: () => {
            openPrivateDeleteConfirmationModal();
          },
          renderWrapper: (content) => (
            <Tip block usePortal content={isDeleteDisabled && 'Cannot delete items with Global ownership'}>
              {content}
            </Tip>
          ),
        },
      ])
    : [];

  return (
    <span data-testid="privates-action-menu">
      <TableRowOptionsMenu
        usePortal
        // adjust offset if adding different menu options
        portalProps={{
          rightOffset: 150,
        }}
        options={options}
        menuClassName="privates-actions-menu-dropdown"
        triggerClassName="privates-actions-menu-trigger"
      />

      {privateDeleteConfirmationModalOpen && item && (
        <PrivateDeleteConfirmationModal
          items={[item]}
          onCancelDelete={closePrivateDeleteConfirmationModal}
          onDeleteComplete={onDeleteComplete}
        />
      )}
      {showConfigureBenchmarksModal && hasPermission('CONFIGURE_BENCHMARKS') && item && (
        <ConfigureBenchmarksModal
          name={item.name}
          itemId={{
            privateFundId: item.itemType === 'INVESTMENT' ? item.id : undefined,
            privatePortfolioId: item.itemType === 'PORTFOLIO' ? item.id : undefined,
          }}
          onSubmit={closeConfigureBenchmarkModal}
          onClose={closeConfigureBenchmarkModal}
          data-testid="qa-configure-benchmarks-modal"
          benchmarkContext={item.itemType === 'INVESTMENT' ? 'private-investment' : 'private-portfolio'}
        />
      )}
    </span>
  );
};

const Tip = styled(Tooltip)`
  height: 100%;
`;
